import { CommonGuidedToursSteps, GuidedTourPayloadData } from './types';
import { GzCarouselInfoOverlay } from '../ui/gzCarouselInfoCard/component/GzCarouselInfoOverlay';
import { CarouselInfoCardEvents } from '../ui/gzCarouselInfoCard/types';
import { GzAnimatedHighlightBoxManager } from '../ui/gzAnimatedHighlightBox';
import { ConsoleRequest, DataProviderType } from '../utils/request';
import { GzCarouselInfoCardWebComponent } from '../ui/gzCarouselInfoCard';

export class GuidedToursManager {
  private static guidedToursStateMap: Map<string, GuidedTourPayloadData> = new Map<string, GuidedTourPayloadData>();
  private static guidedToursCarouselComponentMap = new Map<string, GzCarouselInfoCardWebComponent>();
  private static sectionsWithGuidedTour: Array<string> = [];

  /**
   * Register a section to have a guided tour
   * @param {string} section
   */
  public static registerTour(section: string): void {
    this.registerTourInternal(section);
  }

  /**
   * Handle module change from hydraModule
   */
  public static handleModuleChange(): void {
    this.handleModuleChangeInternal();
  }

  /**
   * Payload.cards, Payload.pointerTargets and Payload.highlightTargets must have the same length.
   * Payload.cards[i] will be shown with the pointerTarget[i] and highlightTarget[i].
   *
   * @param {GuidedTourPayloadData} payload
   */
  public static showTour(payload: GuidedTourPayloadData): void {
    this.showTourInternal(payload);
  }

  /**
   * Hide all blue cards
   */
  public static hideBlueCards(): void {
    this.hideBluCardsInternal();
  }

  /**
   * Show all blue cards
   */
  public static showBlueCards(): void {
    this.showBlueCardsInternal();
  }

  /**
   * Hide the guided tour for the current session
   * @param {string} section
   */
  public static hideTourThisSession(section: string) {
    this.hideTourThisSessionInternal(section);
  }

  /**
   * Get the carousel component for the guided tour
   * @param {string} section
   * @returns {GzCarouselInfoCardWebComponent}
   */
  public static getGuidedTourCarouselComponent(section: string): GzCarouselInfoCardWebComponent {
    return this.getGuidedTourCarouselComponentInternal(section);
  }

  /**
   * Exit the guided tour
   * @param {string} section
   */
  public static exitGuidedTour(section: string): void {
    this.exitGuidedTourInternal(section);
  }

  private static hideTourThisSessionInternal(section: string) {
    const state: GuidedTourPayloadData | undefined = this.guidedToursStateMap.get(section);

    if (!state) {
      return;
    }

    state.hiddenForCurrentSession = true;

    this.guidedToursStateMap.set(section, state);
    GzAnimatedHighlightBoxManager.removeAllHighlights();
  }

  private static getGuidedTourCarouselComponentInternal(section: string) {
    const carouselComponent: GzCarouselInfoCardWebComponent | undefined =
      this.guidedToursCarouselComponentMap.get(section);
    if (!carouselComponent) {
      throw new Error(`Guided Tour Carousel for section ${section} not found`);
    }

    return carouselComponent;
  }

  private static exitGuidedTourInternal(section: string) {
    GzAnimatedHighlightBoxManager.removeAllHighlights();

    const state = this.guidedToursStateMap.get(section);

    if (!state) {
      return;
    }

    const carousel = this.guidedToursCarouselComponentMap.get(section);

    if (!carousel) {
      return;
    }

    carousel.remove();

    state.step = this.getExitTourStep(state);

    this.guidedToursStateMap.set(section, state);
    this.persistGuidedTourState(section, state);
  }

  private static closeTours(): void {
    this.guidedToursCarouselComponentMap.forEach(carouselComponent => {
      carouselComponent.remove();
    });
    GzAnimatedHighlightBoxManager.removeAllHighlights();

    this.guidedToursStateMap.forEach(state => {
      state.highlightTargets = [];
      state.pointerTargets = [];
    });
  }

  private static hideBluCardsInternal() {
    document.body.querySelectorAll('gz-info-overlay').forEach((element: HTMLElement) => {
      element.removeAttribute('isopen');
      element.setAttribute('zindex', '-9999');
      element.style.cssText += 'display: none;';
    });
  }

  private static showBlueCardsInternal() {
    document.body.querySelectorAll('gz-info-overlay').forEach((element: HTMLElement) => {
      element.attributes.setNamedItem(document.createAttribute('isopen'));
      element.setAttribute('zindex', '9999');
      element.style.cssText = element.style.cssText.replace('none', 'block');
    });
  }

  private static showTourInternal(payload: GuidedTourPayloadData) {
    if (!this.isPayloadValid(payload)) {
      console.error('GuidedTourPayloadData is not valid');
      return;
    }

    if (!this.guidedToursStateMap.has(payload.section)) {
      this.guidedToursStateMap.set(payload.section, payload);
    }

    const state = this.guidedToursStateMap.get(payload.section);

    if (!state || !this.shouldShowGuidedTour(state)) {
      return;
    }

    this.setHtmlElementTargetsOnState(state, payload);

    if (this.isGuidedTourAlreadyShown(state)) {
      return;
    }

    const highlightTarget: Array<Element> = this.getHighlightElement(state);
    const carouselComponent: GzCarouselInfoCardWebComponent | undefined = GzCarouselInfoOverlay.show(
      state.section,
      state.cards,
      state.pointerTargets,
      state.closeBtnTooltipText,
      state.step
    );

    if (!carouselComponent) {
      console.error(`Failed to show guided tour for section ${payload.section}`);
      return;
    }

    this.guidedToursCarouselComponentMap.set(state.section, carouselComponent);
    this.addCarouselEventListeners(carouselComponent, state.section);

    GzAnimatedHighlightBoxManager.highlightElements(highlightTarget);
  }

  private static registerTourInternal(section: string) {
    if (this.sectionsWithGuidedTour.includes(section)) {
      return;
    }
    this.sectionsWithGuidedTour.push(section);
  }

  private static handleModuleChangeInternal() {
    this.closeTours();

    let currentPath: string = Application.Main.getRouter().normalizePath();

    if (currentPath.startsWith('/')) {
      currentPath = currentPath.substring(1);
    }

    if (this.sectionHasGuidedTour(currentPath)) {
      this.handleSectionWithGuidedTours(currentPath);
      return;
    }

    this.showBlueCards();
  }

  /**
   * If the guided tour exited on the last card, the step will be set to CommonGuidedToursSteps.STEP_FINISHED
   * If the guided tour was dismissed, the step will be set to CommonGuidedToursSteps.STEP_DISMISSED
   */
  private static getExitTourStep(state: GuidedTourPayloadData): CommonGuidedToursSteps {
    if (state.hiddenForCurrentSession) {
      return state.step;
    }

    return state.step === state.cards.length - 1 // step always starts at 0
      ? CommonGuidedToursSteps.STEP_FINISHED
      : CommonGuidedToursSteps.STEP_DISMISSED;
  }

  private static shouldShowGuidedTour(state: GuidedTourPayloadData): boolean {
    return (
      !state.hiddenForCurrentSession &&
      state.step !== CommonGuidedToursSteps.STEP_DISMISSED &&
      state.step !== CommonGuidedToursSteps.STEP_FINISHED
    );
  }

  /**
   * Check if the section has a guided tour
   *
   * @param {string} section
   * @returns {boolean}
   */
  private static sectionHasGuidedTour(section: string): boolean {
    if (section.startsWith('/')) {
      section = section.substring(1);
    }

    return this.sectionsWithGuidedTour.includes(section);
  }

  private static setHtmlElementTargetsOnState(state: GuidedTourPayloadData, payload: GuidedTourPayloadData): void {
    if (state.pointerTargets.length === 0) {
      state.pointerTargets = payload.pointerTargets;
    }

    if (state.highlightTargets.length === 0) {
      state.highlightTargets = payload.highlightTargets;
    }

    this.guidedToursStateMap.set(payload.section, state);
  }

  private static isGuidedTourAlreadyShown(state: GuidedTourPayloadData): boolean {
    return !!document.querySelector(`gz-carousel-info-card[id="carousel-component-${state.section}"]`);
  }

  private static isPayloadValid(payload: GuidedTourPayloadData): boolean {
    return (
      payload.cards.length === payload.pointerTargets.length && payload.cards.length === payload.highlightTargets.length
    );
  }

  private static addCarouselEventListeners(carouselComponent: GzCarouselInfoCardWebComponent, section: string): void {
    carouselComponent.addEventListener(CarouselInfoCardEvents.PREVIOUS, () => {
      this.handlePreviousEvent(section);
    });

    carouselComponent.addEventListener(CarouselInfoCardEvents.NEXT, () => {
      this.handleNextEvent(section);
    });

    carouselComponent.addEventListener(CarouselInfoCardEvents.CLOSE, () => {
      this.handleCloseEvent(section);
    });

    carouselComponent.addEventListener(CarouselInfoCardEvents.PRIMARY_ACTION, () => {
      this.handlePrimaryActionClicked(section);
    });

    carouselComponent.addEventListener(CarouselInfoCardEvents.SECONDARY_ACTION, () => {
      this.handleSecondaryActionClicked(section);
    });

    carouselComponent.addEventListener(CarouselInfoCardEvents.TERTIARY_ACTION, () => {
      this.handleTertiaryActionClicked(section);
    });
  }

  private static handlePreviousEvent(section: string): void {
    const state = this.guidedToursStateMap.get(section);

    if (!state) {
      return;
    }

    state.step--;

    this.handleStepChange(section, state);
  }

  private static handleNextEvent(section: string): void {
    const state = this.guidedToursStateMap.get(section);

    if (!state) {
      return;
    }

    state.step++;
    this.handleStepChange(section, state);
  }

  private static handleStepChange(section: string, state: GuidedTourPayloadData): void {
    this.guidedToursStateMap.set(section, state);

    GzAnimatedHighlightBoxManager.removeAllHighlights();

    const highlightTargets = this.getHighlightElement(state);

    GzAnimatedHighlightBoxManager.highlightElements(highlightTargets);

    this.persistGuidedTourState(section, state);
  }

  private static getHighlightElement(state: GuidedTourPayloadData): Array<Element> {
    return (
      Array.isArray(state.highlightTargets[state.step])
        ? state.highlightTargets[state.step]
        : [state.highlightTargets[state.step]]
    ) as Array<Element>;
  }

  private static handleCloseEvent(section: string): void {
    this.exitGuidedTour(section);
  }

  private static persistGuidedTourState(section: string, state: GuidedTourPayloadData): void {
    try {
      ConsoleRequest.createRequest({
        dataProviderType: DataProviderType.EPS,
        endpoint: 'AdditionalUserSettings',
        method: 'updateGuidedTour',
        payload: {
          section: section,
          step: state.step,
        },
      });
    } catch (error) {
      console.error('Could not persist guided tour state');
    }
  }

  // implement when required
  private static handlePrimaryActionClicked(section: string): void {
    return;
  }

  // implement when required
  private static handleSecondaryActionClicked(section: string): void {
    return;
  }

  // implement when required
  private static handleTertiaryActionClicked(section: string): void {
    return;
  }

  private static handleSectionWithGuidedTours(section: string): void {
    const state = this.guidedToursStateMap.get(section);

    if (!state) {
      this.hideBlueCards();
      return;
    }

    if (!this.shouldShowGuidedTour(state)) {
      return;
    }

    this.hideBlueCards();
  }
}
